import axiosInstance2 from "../api/axiosInstance2";
import { apiList } from "../api/apiList";
import {
  details as buildVoucherDetails,
  vouchers as seedVouchers,
} from "../pages/voucherExplorer/data";

const useRemoteApi =  true;
const voucherExplorerPath = "voucher-explorer";
const dateTimeOptions = {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

const cloneVoucher = (voucher) => ({
  ...voucher,
  details: {
    ...voucher.details,
    lifecycle: [...(voucher.details?.lifecycle || [])],
  },
});

let mockVouchers = seedVouchers.map(cloneVoucher);

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null);

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const toDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) =>
  toDate(value)?.toLocaleString("en-GB", dateTimeOptions) || "";
const formatIsoDate = (value) => toDate(value)?.toISOString() || "";

const inDateRange = (voucher, from, to) => {
  const date = voucher.details?.placedAtIso?.slice(0, 10);
  return !date || ((!from || date >= from) && (!to || date <= to));
};

const filterMock = (filters = {}) =>
  mockVouchers.filter(
    (voucher) =>
      (!filters.voucherId ||
        String(voucher.id)
          .toLowerCase()
          .includes(filters.voucherId.trim().toLowerCase())) &&
      (!filters.user || voucher.user === filters.user) &&
      (!filters.type || voucher.type === filters.type) &&
      (!filters.status || voucher.status === filters.status) &&
      inDateRange(voucher, filters.from, filters.to),
  );

const getResponseBody = (payload) => payload?.payload ?? payload;

const getResponseItems = (body) => {
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.items)) return body.items;
  if (Array.isArray(body?.vouchers)) return body.vouchers;
  if (Array.isArray(body?.results)) return body.results;
  return [];
};

const normaliseVoucher = (item = {}) => {
  const existingDetails = item.details || {};
  const status = firstDefined(item.status, "Pending");
  const placedDate = firstDefined(item.bettedDate);
  const placedAt = firstDefined(existingDetails.placedAt, formatDate(placedDate), "");
  const placedAtIso = firstDefined(
    existingDetails.placedAtIso,
    formatIsoDate(placedDate),
    "",
  );
  const source = firstDefined(item.placedAtource, item.source, existingDetails.market, "");
  const stake = toNumber(firstDefined(item.amount, item.stake, existingDetails.stake));
  const grossReturn = toNumber(
    firstDefined(item.payOut, item.grossReturn, existingDetails.grossReturn),
  );
  const commission = toNumber(
    firstDefined(
      item.comission,
      existingDetails.commission,
    ),
  );
  const payout = toNumber(
    firstDefined(item.payOut, item.payout, existingDetails.payout),
  );
  const legs = toNumber(
    firstDefined(item.teamCount, item.legs, existingDetails.legs),
    1,
  );
  const outcome = firstDefined(
    item.outcome,
    existingDetails.outcome,
    status,
  );
  const result = toNumber(firstDefined(item.result, status === "Pending" ? 0 : payout - stake));

  return {
    gid: firstDefined(item.id, ""),
    id: firstDefined(item.postingNo, ""),
    posting: firstDefined(item.postingNo, item.posting, ""),
    user: firstDefined(item.user, ""),
    source,
    type: firstDefined(item.gamblingType, item.type, ""),
    teams: firstDefined(
      item.teams,
      item.teamCount != null ? `${item.teamCount}` : undefined,
      legs ? `${legs}` : "",
    ),
    stake,
    result,
    status,
    details: buildVoucherDetails(
      status,
      placedAt,
      placedAtIso,
      source,
      stake,
      grossReturn,
      commission,
      payout,
      legs,
      outcome,
      {
        pending: firstDefined(
          existingDetails.lifecycle?.find(({ key }) => key === "pending")?.at,
          formatDate(placedDate),
        ),
        settled: firstDefined(
          existingDetails.lifecycle?.find(({ key }) => key === "settled")?.at,
          formatDate(firstDefined(item.settledDate)),
        ),
        recalculated: firstDefined(
          existingDetails.lifecycle?.find(({ key }) => key === "recalculated")?.at,
          formatDate(firstDefined(item.recalculatedDate)),
        ),
        final: firstDefined(
          existingDetails.lifecycle?.find(({ key }) => key === "final")?.at,
          formatDate(firstDefined(item.transactionDate)),
        ),
      },
    ),
  };
};

const normaliseList = (payload) => {
  const body = getResponseBody(payload);
  const items = getResponseItems(body).map(normaliseVoucher);
  return {
    items,
    total: firstDefined(body?.total, body?.count, items.length),
  };
};

export const fetchVouchersFromApi = async (filters = {}) => {
  const response = await axiosInstance2.post(apiList.getVoucherExplorerApi, filters);
  return normaliseList(response.data);
};

export const fetchVouchersDetailsFromApi = async (voucherId) => {
  const response = await axiosInstance2.get(
    `${apiList.getVoucherExplorerDetailsApi}/${encodeURIComponent(voucherId)}`,
  );
  return response.data?.payload || response.data;
};

export const fetchVouchersFromMock = async (filters = {}) => {
  await new Promise((resolve) => setTimeout(resolve, 250));
  const items = filterMock(filters);
  return { items, total: items.length };
};

export const fetchVouchers = (filters = {}) =>
  useRemoteApi ? fetchVouchersFromApi(filters) : fetchVouchersFromMock(filters);

export const rejectVoucherFromApi = async (voucherId) => {
  const response = await axiosInstance2.patch(
    `${voucherExplorerPath}/${encodeURIComponent(voucherId)}/reject`,
  );
  return getResponseBody(response.data);
};

export const rejectVoucherFromMock = async (voucherId) => {
  await new Promise((resolve) => setTimeout(resolve, 350));
  const rejectedAt = new Date().toLocaleString("en-GB", dateTimeOptions);
  let updatedVoucher;

  mockVouchers = mockVouchers.map((voucher) => {
    if (String(voucher.id) !== String(voucherId)) return voucher;

    updatedVoucher = {
      ...voucher,
      status: "Rejected",
      result: -toNumber(voucher.stake),
      details: {
        ...voucher.details,
        outcome: "Rejected",
        grossReturn: 0,
        payout: 0,
        netResult: -toNumber(voucher.stake),
        lifecycle: [
          ...(voucher.details?.lifecycle || []).filter(
            ({ key }) => key === "placement",
          ),
          {
            key: "rejected",
            label: "Rejected",
            at: rejectedAt,
            state: "failed",
          },
        ],
      },
    };
    return updatedVoucher;
  });

  return updatedVoucher;
};

export const rejectVoucher = (voucherId) =>
  useRemoteApi
    ? rejectVoucherFromApi(voucherId)
    : rejectVoucherFromMock(voucherId);

const sampleVoucher = {
  Id: 56,
  PostingNo: "GB34df343434",
  Amount: 5000,
  TeamCount: 3,
  Source: "Mobile",
  Comission: 45,
  PayOut: 5666,
  BettedDate: "2026-09-23T20:00:00",
  GamblingType: "Single",
  Status: "Pending",
  Outcome: "Win",
  TransactionDate: "2026-09-23T22:30:00",
  User: "j.smith",
};

export const fetchSampleVoucher = async () => {
  if (useRemoteApi) {
    const response = await axiosInstance2.get(`${voucherExplorerPath}/sample`);
    return normaliseList(response.data);
  }

  await new Promise((resolve) => setTimeout(resolve, 250));
  return normaliseList([sampleVoucher]);
};
